import { Link } from 'react-router-dom';
import { UserProfile } from '@shared/types';
import { User } from 'firebase/auth';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/common/SEO';

// Image constants for easy replacement
const IMAGES = {
  hero: '/images/hero-organization.png',
  playerBenefits: '/images/player-benefits.png',
  operatorBenefits: '/images/operator-benefits.png',
  mobileApps: '/images/mobile-apps.png'
};

interface HowItWorksPageProps {
  userProfile?: UserProfile | null;
  user?: User | null;
}

export function HowItWorksPage({ userProfile, user }: HowItWorksPageProps) {
  const handleContactClick = () => {
    const subject = encodeURIComponent('WaitlistPoker Demo Request - Interested in Learning More');
    const body = encodeURIComponent(
      `Hi there,\n\nI'm interested in learning more about WaitlistPoker and would like to book a demo to see the product in action.\n\nPlease let me know:\n- Available demo times\n- Pricing information\n- How to get started\n\nThank you!`
    );
    window.location.href = `mailto:support@pingsolveai.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO title="How It Works – WaitlistPoker" path="/how-it-works" />
      <Header userProfile={userProfile || null} user={user || null} />
      
      {/* Hero Section */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Stop Managing Lists.
                <span className="text-primary"> Start Running Better Games.</span>
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Transform your poker room operations with real-time waitlist management and seat availability notifications. 
                Keep players happy, staff efficient, and tables full. Smarter waitlists. From $199/month. 
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleContactClick}
                  className="btn btn-primary text-lg px-8 py-4 rounded-xl font-semibold hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Book a Demo & Get Started
                </button>
                <Link
                  to="/"
                  className="btn btn-outline text-lg px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
                >
                  View Live Rooms
                </Link>
              </div>

              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                  <span>10-minute setup</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                  <span>30-day guarantee</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-secondary rounded-full mr-2"></div>
                  <span>Free mobile app</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <img
                  src={IMAGES.hero}
                  alt="WaitlistPoker dashboard showing organized table management and real-time updates"
                  className="max-w-full max-h-full object-contain rounded-2xl"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden flex-col items-center justify-center text-center p-8">
                  <div className="w-24 h-24 bg-primary/20 rounded-2xl flex items-center justify-center mb-4">
                    <span className="text-4xl">📊</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Organized Efficiency</h3>
                  <p className="text-gray-500 text-sm">Replace this with your hero image</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Poker Players Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              For Poker Players
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Never miss a seat again. Get real-time updates and know exactly where you stand in line.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🔔</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Real-Time Notifications</h3>
              <p className="text-gray-600">
                Get instant notifications when your seat opens up at your favorite tables. No more asking staff for updates.
              </p>
            </div>

            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Mobile Convenience</h3>
              <p className="text-gray-600">
                Join waitlists and check availability from anywhere using our free iOS app.
              </p>
            </div>

            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">👥</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Transparent Waitlists</h3>
              <p className="text-gray-600">
                See exactly where you are in line and how long you might wait. No more guessing games.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Poker Room Operators Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              For Poker Room Operators
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Eliminate chaos, maximize revenue, and keep your players happy with streamlined operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10-Minute Setup</h3>
              <p className="text-gray-600">
                Get up and running in minutes with zero installation required. No technical headaches.
              </p>
            </div>

            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Maximize Revenue</h3>
              <p className="text-gray-600">
                Keep tables full and games running smoothly. More efficient operations mean more rake.
              </p>
            </div>

            <div className="card card-hover text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🛡️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">30-Day Guarantee</h3>
              <p className="text-gray-600">
                Try it risk-free with our 30-day money-back guarantee. See the difference in your first month.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Selling Points Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose WaitlistPoker?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built specifically for poker rooms, designed for maximum efficiency and player satisfaction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time seat availability notifications</h3>
                  <p className="text-gray-600">Send notifications to inform players when seats open up at cash tables</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Streamlined waitlist management</h3>
                  <p className="text-gray-600">Efficiently manage and fill open seats with automated processes</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Mobile accessibility</h3>
                  <p className="text-gray-600">Manage cash games and waitlists wirelessly from any device (smartphone, tablet, laptop, etc.)</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Operational efficiency</h3>
                  <p className="text-gray-600">Reduce the need for staff to manually track waitlists or table availability</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Revenue growth</h3>
                  <p className="text-gray-600">Maximize rake by keeping tables full and games running</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Quick setup (10 minutes)</h3>
                  <p className="text-gray-600">Get started in minutes with zero installation required</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">30-day money-back guarantee</h3>
                  <p className="text-gray-600">Try it risk-free and see the difference in your operations</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">✓</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Free mobile app (iOS)</h3>
                  <p className="text-gray-600">Native apps for players with full functionality</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Poker Room?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Book a demo today and see how WaitlistPoker can streamline your operations, 
            keep players happy, and maximize your revenue.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContactClick}
              className="bg-white text-primary hover:bg-gray-50 text-lg px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Book a Demo & Get Started
            </button>
            <Link
              to="/"
              className="border-2 border-white text-white hover:bg-white hover:text-primary text-lg px-8 py-4 rounded-xl font-semibold transition-all duration-200"
            >
              View Live Rooms
            </Link>
          </div>

          <div className="mt-8 text-primary-100 text-sm">
            <p>✓ 10-minute setup • ✓ 30-day guarantee • ✓ Free mobile app</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
